import { useState } from 'react'
import { Checkbox } from '../../components/inputs/Checkbox'
import { TextInput } from '../../components/inputs/textInput'
import type { Permission } from '../../redux-toolkits/auth/auth.type'

const moduleLabels: Record<string, string> = {
  vendor: 'Creators',
  order: 'Orders',
  product: 'Products',
  transaction: 'Transactions',
  pos: 'POS',
  audits_logs: 'Audit Logs',
}

const getModuleLabel = (section: string): string =>
  moduleLabels[section] ??
  section.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())

export const GroupedPermissionSelector = ({
  permissions,
  selectedPermissions,
  onChange,
}: {
  permissions: Permission[]
  selectedPermissions: string[]
  onChange: (permissions: string[]) => void
}) => {
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  const groups = new Map<string, Permission[]>()

  for (const permission of permissions) {
    const label = getModuleLabel(permission.section || 'other')
    const group = groups.get(label) ?? []
    group.push(permission)
    groups.set(label, group)
  }

  const visibleGroups = Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([label, modulePermissions]) => ({
      label,
      total: modulePermissions.length,
      selected: modulePermissions.filter((permission) =>
        selectedPermissions.includes(permission._id),
      ).length,
      permissions: modulePermissions.filter((permission) =>
        `${label} ${permission.code} ${permission.description}`
          .toLowerCase()
          .includes(query),
      ),
    }))
    .filter((group) => group.permissions.length > 0)

  return (
    <div className="mt-3 flex flex-col gap-3">
      <TextInput
        type="search"
        aria-label="Search modules or permissions"
        placeholder="Search modules or permissions"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        hasIcon={false}
      />
      <div className="max-h-72 overflow-y-auto flex flex-col gap-2">
        {visibleGroups.map((group) => (
          <details
            key={group.label}
            open={query ? true : undefined}
            className="rounded-md border border-divider-gray"
          >
            <summary className="cursor-pointer p-3 text-sm font-medium focus-visible:outline focus-visible:outline-2">
              {group.label}
              <span className="ml-2 text-xs text-sec-black">
                {group.selected} / {group.total} selected
              </span>
            </summary>
            <div className="border-t border-divider-gray p-3 flex flex-col gap-4">
              {group.permissions.map((permission) => (
                <Checkbox
                  key={permission._id}
                  checkStatus={selectedPermissions.includes(permission._id)}
                  handleChange={(checked) =>
                    onChange(
                      checked
                        ? [...selectedPermissions, permission._id]
                        : selectedPermissions.filter(
                            (id) => id !== permission._id,
                          ),
                    )
                  }
                  className="focus-within:outline focus-within:outline-2"
                  text={
                    <span className="block">
                      <span className="block text-sm font-medium break-words">
                        {permission.code.replace(/_/g, ' ')}
                      </span>
                      <span className="block text-xs text-sec-black">
                        {permission.description}
                      </span>
                    </span>
                  }
                />
              ))}
            </div>
          </details>
        ))}
        {visibleGroups.length === 0 && (
          <p className="p-3 text-sm text-sec-black">
            {query ? 'No matching permissions.' : 'No permissions available.'}
          </p>
        )}
      </div>
    </div>
  )
}
